import { useTranslation } from "react-i18next";
import type { SegmentedControlOption } from "src/common/components/SegmentedControl/SegmentedControl";

export type StatusFilter = "all" | "completed" | "inProgress" | "notStarted";

interface StatusCounts {
  all: number;
  completed: number;
  inProgress: number;
  notStarted: number;
}

const StatusIcon = ({ status, isActive }: { status: StatusFilter; isActive: boolean }) => {
  const paths: Record<StatusFilter, string> = {
    all: "M4 6h16M4 10h16M4 14h16M4 18h16",
    completed: "M5 13l4 4L19 7",
    inProgress: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
    notStarted: "M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  };

  const activeColors: Record<StatusFilter, string> = {
    all: "text-gray-600",
    completed: "text-green-600",
    inProgress: "text-yellow-600",
    notStarted: "text-blue-600",
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`h-4 w-4 ${isActive ? activeColors[status] : "text-gray-400"}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={paths[status]} />
    </svg>
  );
};

export const useEnduserStatusOptions = (
  counts: StatusCounts,
  activeStatus: StatusFilter,
): SegmentedControlOption<StatusFilter>[] => {
  const { t } = useTranslation();

  const tabs: {
    key: StatusFilter;
    label: string;
    activeColor: string;
    activeBgColor: string;
  }[] = [
    { key: "all", label: t("enduserStats.status.all"), activeColor: "text-gray-700", activeBgColor: "bg-gray-100" },
    { key: "notStarted", label: t("enduserStats.status.notStarted"), activeColor: "text-blue-700", activeBgColor: "bg-blue-100" },
    { key: "inProgress", label: t("enduserStats.status.inProgress"), activeColor: "text-yellow-700", activeBgColor: "bg-yellow-100" },
    { key: "completed", label: t("enduserStats.status.completed"), activeColor: "text-green-700", activeBgColor: "bg-green-100" },
  ];

  return tabs.map((tab) => ({
    value: tab.key,
    label: tab.label,
    icon: <StatusIcon status={tab.key} isActive={activeStatus === tab.key} />,
    badge: counts[tab.key],
    activeColor: tab.activeColor,
    activeBgColor: tab.activeBgColor,
  }));
};
